import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LeaderboardScreen from './LeaderboardScreen';

// Nespresso brand colors
const NESPRESSO = {
  brown: '#986f38',
  gold: '#c69f36',
  black: '#000000',
  darkGray: '#292929',
};

interface AttractScreenCarouselProps {
  intervalMs?: number;
  onInteraction?: () => void;
}

// Text attract screen content
const TEXT_SCREENS = [
  {
    id: 'rewards',
    headline: 'Rewards as rich as your espresso',
    subtext: 'Recycle your capsules and unlock exclusive perks',
  },
  {
    id: 'count-twice',
    headline: 'Make your coffee count twice',
    subtext: 'Every capsule recycled earns you points towards your next reward',
  },
  {
    id: 'milestone',
    headline: 'Your next milestone is brewing',
    subtext: 'Keep recycling to reach new tiers and unlock premium benefits',
  },
];


// Text Attract Screen Component
const TextAttractScreen: React.FC<{ headline: string; subtext: string }> = ({
  headline,
  subtext,
}) => {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <h2 className="text-5xl font-light text-white mb-6 leading-tight">
          {headline}
        </h2>
        <p className="text-xl text-white/60 font-light max-w-lg">
          {subtext}
        </p>
      </motion.div>
    </div>
  );
};

// Screen type definitions
type Screen =
  | { type: 'text'; id: string; headline: string; subtext: string }
  | { type: 'leaderboard'; id: string };

const SCREENS: Screen[] = [
  { type: 'text', ...TEXT_SCREENS[0] },
  { type: 'text', ...TEXT_SCREENS[1] },
  { type: 'text', ...TEXT_SCREENS[2] },
  { type: 'leaderboard', id: 'leaderboard' },
];

const AttractScreenCarousel: React.FC<AttractScreenCarouselProps> = ({
  intervalMs = 6000,
  onInteraction,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [idleTimer, setIdleTimer] = useState<NodeJS.Timeout | null>(null);

  // Handle user interaction - pause carousel
  const handleInteraction = useCallback(() => {
    setIsPaused(true);
    onInteraction?.();

    // Clear existing idle timer
    if (idleTimer) {
      clearTimeout(idleTimer);
    }

    // Resume after 10 seconds of inactivity
    const timer = setTimeout(() => {
      setIsPaused(false);
    }, 10000);
    setIdleTimer(timer);
  }, [idleTimer, onInteraction]);

  // Auto-advance carousel
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % SCREENS.length);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [intervalMs, isPaused]);

  // Cleanup idle timer on unmount
  useEffect(() => {
    return () => {
      if (idleTimer) {
        clearTimeout(idleTimer);
      }
    };
  }, [idleTimer]);

  const currentScreen = SCREENS[currentIndex];

  // Animation variants for screen transitions
  const variants = {
    enter: {
      opacity: 0,
      scale: 0.98,
    },
    center: {
      opacity: 1,
      scale: 1,
    },
    exit: {
      opacity: 0,
      scale: 1.02,
    },
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ backgroundColor: NESPRESSO.black }}
      onClick={handleInteraction}
      onMouseMove={handleInteraction}
      onTouchStart={handleInteraction}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentScreen.id}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          className="absolute inset-0"
        >
          {currentScreen.type === 'text' ? (
            <TextAttractScreen
              headline={currentScreen.headline}
              subtext={currentScreen.subtext}
            />
          ) : (
            <LeaderboardScreen />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Progress indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {SCREENS.map((screen, index) => (
          <button
            key={screen.id}
            onClick={e => {
              e.stopPropagation();
              setCurrentIndex(index);
              handleInteraction();
            }}
            className="w-2 h-2 rounded-full transition-all duration-300"
            style={{
              backgroundColor:
                index === currentIndex
                  ? NESPRESSO.gold
                  : 'rgba(255, 255, 255, 0.3)',
              transform: index === currentIndex ? 'scale(1.2)' : 'scale(1)',
            }}
            aria-label={`Go to screen ${index + 1}`}
          />
        ))}
      </div>

      {/* Pause indicator */}
      {isPaused && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute top-4 right-4 text-white/40 text-sm font-light"
        >
          Paused
        </motion.div>
      )}
    </div>
  );
};

export default AttractScreenCarousel;
export { TextAttractScreen, LeaderboardScreen };
